import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import PropTypes from 'prop-types'

// Utils
import history from '../../../../history'
import { OVERLAY_HELPERS } from '../../../../constants/navigation'
import { useTrackingContext } from '../../../../providers/tracking'
import { TrackingEventConstants } from '../../../../constants/tracking'

// Actions
import {
  resetClientStatus,
  setClientIsSubscribedToBuy,
  setClientIsSubscribedToHome,
  setClientIsSubscribedToHomeMarket,
  setClientIsSubscribedToListings,
  setClientIsUnsubscribedFromCustomer,
  setClientOptedIntoSms
} from '../../../../actions/client'

// Selectors
import { selectShouldRenderSmsOptInFlow } from '../../../../store/selectors/display'
import { selectHomebotListingsFeatureEnabled } from '../../../../store/selectors/customerProfile'
import {
  selectClientEmail,
  selectClientMobile,
  selectClientIsBuyer,
  selectIsHomeowner,
  selectClientHasSubscribedToSms,
  selectClientIsSubscribedToBuy,
  selectClientIsSubscribedToHome,
  selectClientIsSubscribedToHomebotListings,
  selectClientIsSubscribedToHomeMarket,
  selectClientIsSubscribedToSms,
  selectClientIsTwilioBlacklisted
} from '../../../../store/selectors/client'
import { selectCompanyName, selectCustomerFirstName } from '../../../../store/selectors/customerProfile'

// Components
import { Text, Button, CheckBox, Format } from '../../..'
import { SlideOnMount } from '../../Animations'
import { IconPencil } from '../../Icon'
import { TEXT_UPDATE_PAGES } from '../../TextUpdatesPanel'

// Styles
import styles from './ManagePreferences.module.scss'

// Assets
import EnvelopeHB from './assets/EnvelopeHB.svg'
import MobileHB from './assets/MobileHB.svg'

const checkMessages = defineMessages({
  buyerReports: {
    id: 'Preferences.panel.check.buyerReports',
    defaultMessage: 'Your buyer reports'
  },
  homeMarket: {
    id: 'Preferences.panel.check.homeMarket',
    defaultMessage: 'Home market update'
  },
  homeReports: {
    id: 'Preferences.panel.check.homeReports',
    defaultMessage: 'Home value updates'
  },
  listings: {
    id: 'Preferences.panel.check.listings',
    defaultMessage: 'Home search alerts'
  }
})

const PreferencesPanel = ({ setShowEmailPreferencesPanel }) => {
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()

  const customerFirstName = useSelector(selectCustomerFirstName)
  const companyName = useSelector(selectCompanyName)

  const clientEmail = useSelector(selectClientEmail)
  const clientMobile = useSelector(selectClientMobile)
  const hasHomebotListingsFeature = useSelector(selectHomebotListingsFeatureEnabled)
  const isBuyer = useSelector(selectClientIsBuyer)
  const isHomeowner = useSelector(selectIsHomeowner)

  const subscribedToBuyerReport = useSelector(selectClientIsSubscribedToBuy)
  const subscribedToHomebotListings = useSelector(selectClientIsSubscribedToHomebotListings)
  const subscribedToHomeMarketReports = useSelector(selectClientIsSubscribedToHomeMarket)
  const subscribedToHomeReports = useSelector(selectClientIsSubscribedToHome)

  const hasSubscribedToSms = useSelector(selectClientHasSubscribedToSms)
  const outViaTwilio = useSelector(selectClientIsTwilioBlacklisted)
  const renderSmsOptIn = useSelector(selectShouldRenderSmsOptInFlow)
  const subscribedToHomeReportsSms = useSelector(selectClientIsSubscribedToSms)

  const navToEmailUpdates = () => {
    if (setShowEmailPreferencesPanel) {
      setShowEmailPreferencesPanel(false)
    }
    history.push(OVERLAY_HELPERS.emailUpdates.buildPath())
  }
  const navToTextUpdates = key => {
    dispatch(resetClientStatus())
    history.push(OVERLAY_HELPERS.textUpdates.buildPath(key))
  }

  const handleBuyerReportsToggle = e => {
    const toggleDirection = e.target.checked
      ? TrackingEventConstants.TOGGLE_SUBSCRIBE
      : TrackingEventConstants.TOGGLE_UNSUBSCRIBE
    trackingClient?.toggleBuyerReportUnsubscribed({
      toggle_click: toggleDirection
    })
    dispatch(setClientIsSubscribedToBuy({ setTo: e.target.checked }))
  }

  const handleHomeMarketReportsToggle = e => dispatch(setClientIsSubscribedToHomeMarket({ setTo: e.target.checked }))

  const handleHomeReportsToggle = e => {
    const toggleDirection = e.target.checked
      ? TrackingEventConstants.TOGGLE_SUBSCRIBE
      : TrackingEventConstants.TOGGLE_UNSUBSCRIBE
    trackingClient?.toggleHomeReportUnsubscribed({
      toggle_click: toggleDirection
    })
    dispatch(setClientIsSubscribedToHome({ setTo: e.target.checked }))
  }

  const handleListingsToggle = e => dispatch(setClientIsSubscribedToListings({ setTo: e.target.checked }))

  const handleUnsubscribeFromCustomer = () => {
    trackingClient?.webPanelUnsubscribed()
    dispatch(setClientIsUnsubscribedFromCustomer({ setTo: true }))
  }

  const handleHomeReportsSMSToggle = e => {
    if (outViaTwilio) {
      e.target.checked = subscribedToHomeReportsSms
      navToTextUpdates(TEXT_UPDATE_PAGES.TWILIO)
    } else {
      dispatch(setClientOptedIntoSms({ setTo: e.target.checked }))
    }
  }

  return (
    <>
      <SlideOnMount className={styles.header}>
        <Text size='xl' variant='neutralCool' variantCount='900'>
          <FormattedMessage id='Preferences.panel.title' defaultMessage='How should we send updates?' />
        </Text>
        <Text variant='neutralCool' variantCount='700'>
          <FormattedMessage
            id='Preferences.panel.subTitle'
            defaultMessage='This is how we notify you about changes in your dashboard and timely wealth-building tips'
          />
        </Text>
      </SlideOnMount>
      <SubSection icon={EnvelopeHB}>
        <Text size='l' variant='neutralCool' variantCount='900'>
          <FormattedMessage id='Preferences.panel.sub.emails.title' defaultMessage='Emails' />
        </Text>
        <div className={styles.inline}>
          <Text variant='neutralCool' variantCount='700' element='span'>
            {clientEmail}
          </Text>
          <Button className={styles.editButton} minimal iconStart={IconPencil} onClick={navToEmailUpdates} />
        </div>
        <div className={styles.sliders}>
          {!!isHomeowner && (
            <SliderSection
              name='Home Digests Toggle'
              defaultChecked={subscribedToHomeReports}
              handleToggle={handleHomeReportsToggle}
              message={checkMessages.homeReports}
            />
          )}
          {!!isHomeowner && !isBuyer && (
            <SliderSection
              name='Home Market Reports Toggle'
              defaultChecked={subscribedToHomeMarketReports}
              handleToggle={handleHomeMarketReportsToggle}
              message={checkMessages.homeMarket}
            />
          )}
          {!!isBuyer && (
            <SliderSection
              name='Buyer Reports Toggle'
              defaultChecked={subscribedToBuyerReport}
              handleToggle={handleBuyerReportsToggle}
              message={checkMessages.buyerReports}
            />
          )}
          {hasHomebotListingsFeature && (
            <SliderSection
              name='Listings Toggle'
              defaultChecked={subscribedToHomebotListings}
              handleToggle={handleListingsToggle}
              message={checkMessages.listings}
            />
          )}
        </div>
      </SubSection>
      {renderSmsOptIn && (
        <SubSection icon={MobileHB}>
          <Text size='l' variant='neutralCool' variantCount='900'>
            <FormattedMessage id='Preferences.panel.sub.mobile.title' defaultMessage='Text Messages' />
          </Text>
          {clientMobile && (
            <div className={styles.inline}>
              <Text variant='neutralCool' variantCount='700' element='span'>
                <Format.Phone value={clientMobile} />
              </Text>
              <Button
                className={styles.editButton}
                minimal
                iconStart={IconPencil}
                onClick={() => navToTextUpdates(TEXT_UPDATE_PAGES.UPDATE)}
              />
            </div>
          )}
          {hasSubscribedToSms && (
            <div className={styles.sliders}>
              <SliderSection
                name='Home Digests SMS Toggle'
                defaultChecked={subscribedToHomeReportsSms}
                handleToggle={handleHomeReportsSMSToggle}
                message={checkMessages.homeReports}
              />
            </div>
          )}
          {!hasSubscribedToSms && (
            <Button className={styles.smsButton} onClick={() => navToTextUpdates(TEXT_UPDATE_PAGES.OPT_IN)}>
              <FormattedMessage id='Preferences.panel.sub.explainer.sms' defaultMessage='Receive digests via text' />
            </Button>
          )}
        </SubSection>
      )}
      <SlideOnMount className={styles.header}>
        <Text size='l' variant='neutralCool' variantCount='900'>
          <FormattedMessage
            id='Preferences.panel.unsubFromCustomer.title'
            defaultMessage='Want to unsubscribe entirely?'
          />
        </Text>
        <Text variant='neutralCool' variantCount='700'>
          <FormattedMessage
            id='Preferences.panel.unsubFromCustomer.subTitle'
            defaultMessage='Unsubscribe to stop receiving any Homebot emails from {customerFirstName}'
            values={{
              customerFirstName: customerFirstName
            }}
          />
        </Text>
        <Button
          secondary
          className={styles.unsubscribeButton}
          data-qa='unsubscribeFromCustomerButton'
          onClick={handleUnsubscribeFromCustomer}
        >
          <FormattedMessage id='Preferences.panel.unsubFromCustomer.unsubscribe' defaultMessage='Unsubscribe' />
        </Button>
        <Text variant='neutralCool' variantCount='700'>
          <FormattedMessage
            id='Preferences.panel.unsubFromCustomer.subTitle.note'
            defaultMessage='Note, this selection does not affect other emails you may receive from {companyName}. Homebot is an unaffiliated third-party. To unsubscribe more broadly, you will need to reach out directly to {customerFirstName} and/or {companyName}.'
            values={{
              customerFirstName: customerFirstName,
              companyName: companyName
            }}
          />
        </Text>
      </SlideOnMount>
    </>
  )
}

PreferencesPanel.propTypes = {
  setShowEmailPreferencesPanel: PropTypes.func
}

export const SubSection = ({ icon: Icon, children }) => (
  <SlideOnMount className={styles.subSection}>
    <div className={styles.iconWrap}>
      <Icon />
    </div>
    {children}
  </SlideOnMount>
)
SubSection.propTypes = {
  icon: PropTypes.any,
  children: PropTypes.any
}

const SliderSection = ({ name, defaultChecked, handleToggle, message }) => {
  const intl = useIntl()

  return (
    <CheckBox.Slider
      className={styles.sliderButton}
      name={name}
      defaultChecked={defaultChecked}
      onChange={handleToggle}
    >
      <Text className={styles.sliderText} size='l' variant='neutralCool' variantCount='700' element='span'>
        {intl.formatMessage(message)}
      </Text>
    </CheckBox.Slider>
  )
}
SliderSection.propTypes = {
  name: PropTypes.string,
  defaultChecked: PropTypes.bool,
  handleToggle: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.string,
    defaultMessage: PropTypes.string
  })
}

export default PreferencesPanel
